import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Lock, ChevronRight, UserPlus, Search, Loader2, MessageCircle, MessagesSquare, Clock, Image as ImageIcon, Pin } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ChatThread from "@/components/ChatThread";
import { ColdStartDiscover } from "@/components/chat/ColdStartDiscover";
import { NewCircleDialog } from "@/components/chat/NewCircleDialog";
import { getE2eePreview } from "@/lib/crypto/previewStore";
import { WhatsNewCard } from "@/components/common/WhatsNewCard";
import { ActivationChecklist } from "@/components/common/ActivationChecklist";
import { InstallPrompt } from "@/components/common/InstallPrompt";
import { GettingStartedNudge } from "@/components/common/GettingStartedNudge";
import { StoriesBar } from "@/components/stories/StoriesBar";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck, TrustBand } from "@/components/common/TrustBadge";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";
import { OfficialBadge } from "@/components/common/OfficialBadge";
import { isOfficialUser } from "@/lib/official";
import { SearchButton, NotifBell, ActivityBell } from "@/components/common/HeaderActions";
import { ChatRowActions } from "@/components/chat/ChatRowActions";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Cold-load-safe preview labels for non-text message kinds (from backend `last_message.kind`).
const KIND_LABEL = {
  image: "Photo",
  photo: "Photo",
  album: "Photos",
  gif: "GIF",
  sticker: "Sticker",
  poll: "Poll",
  checklist: "Checklist",
  contact: "Contact card",
  crypto: "Crypto transfer",
  file: "File",
  voice: "Voice message",
  video: "Video",
  location: "Location",
};
const PHOTO_KINDS = new Set(["image", "photo", "album", "gif"]);

function LastPreview({ chat }) {
  const lm = chat.last_message;
  if (!lm) return <span>No messages yet</span>;
  if (lm.e2ee) {
    const pv = getE2eePreview(chat.id);
    let isImg = false;
    if (pv && pv.img) {
      if (!lm.at || !pv.at) isImg = true;
      else isImg = new Date(pv.at).getTime() >= new Date(lm.at).getTime() - 2000;
    }
    return (
      <span className="flex items-center gap-1 truncate text-bloq-mint/90">
        {isImg ? <ImageIcon className="h-3.5 w-3.5 shrink-0" /> : <Lock className="h-3 w-3 shrink-0" />}
        {isImg ? "Encrypted photo" : "Encrypted message"}
      </span>
    );
  }
  if (lm.kind === "deleted") return <span className="truncate italic text-bloq-muted">Message deleted</span>;
  // Cold-load: use the backend-provided `kind` so photos/stickers/polls show correctly even before opening the chat.
  if (lm.kind && lm.kind !== "text" && KIND_LABEL[lm.kind]) {
    const isPhoto = PHOTO_KINDS.has(lm.kind);
    return (
      <span className="flex items-center gap-1 truncate">
        {isPhoto && <ImageIcon className="h-3.5 w-3.5 shrink-0" />}
        {lm.text || KIND_LABEL[lm.kind]}
      </span>
    );
  }
  if (lm.image && !lm.text) {
    return (
      <span className="flex items-center gap-1 truncate">
        <ImageIcon className="h-3.5 w-3.5 shrink-0" /> Photo
      </span>
    );
  }
  return <span className="truncate">{lm.text || "No messages yet"}</span>;
}

function connKind(status) {
  const s = String(status || "none").toLowerCase();
  if (s === "connected" || s === "friends") return "connected";
  if (s === "none" || s === "stranger") return "none";
  if (s.includes("incoming") || s.endsWith("_in") || s === "received" || s === "awaiting_you") return "incoming";
  return "pending"; // pending_out / requested / outgoing / sent / blocked
}

function SearchResultRow({ u, onOpen }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["conn-status", u.id],
    queryFn: () => api.connectionStatus(u.id),
    staleTime: 30000,
  });
  const kind = connKind(data?.status);
  const clickable = kind === "connected";

  const connect = async (e) => {
    e.stopPropagation();
    setSending(true);
    try {
      await api.sendConnectionRequest(u.id);
      toast.success(`Connection request sent to @${u.username}`);
      qc.setQueryData(["conn-status", u.id], { status: "pending" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={clickable ? () => onOpen(u.id) : undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors",
        clickable && "cursor-pointer hover:bg-white/5",
      )}
      data-testid={`search-result-${u.username}`}
    >
      <Avatar user={u} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold">{u.display_name}</span>
          {u.verified && <VerifiedCheck size={12} />}
          <TopConnectorCrown match={u} iconOnly />
          {u.score_band && <TrustBand band={u.score_band} />}
        </div>
        <div className="truncate text-xs text-bloq-muted">@{u.username}</div>
      </div>
      <div className="shrink-0" data-testid={`conn-action-${u.username}`}>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" />
        ) : kind === "connected" ? (
          <button onClick={(e) => { e.stopPropagation(); onOpen(u.id); }} className="flex items-center gap-1 rounded-full border border-bloq-mint/40 bg-bloq-mint/10 px-3 py-1.5 text-xs font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/15" data-testid={`msg-btn-${u.username}`}>
            <MessageCircle className="h-3.5 w-3.5" /> Message
          </button>
        ) : kind === "incoming" ? (
          <button onClick={(e) => { e.stopPropagation(); navigate("/app/requests"); }} className="flex items-center gap-1 rounded-full border border-bloq-blue/40 bg-bloq-blue/10 px-3 py-1.5 text-xs font-semibold text-bloq-blue transition-colors hover:bg-bloq-blue/15" data-testid={`respond-btn-${u.username}`}>
            <UserPlus className="h-3.5 w-3.5" /> Respond
          </button>
        ) : kind === "pending" ? (
          <span className="flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted" data-testid={`pending-badge-${u.username}`}>
            <Clock className="h-3.5 w-3.5" /> Requested
          </span>
        ) : (
          <button onClick={connect} disabled={sending} className="flex items-center gap-1 rounded-full bloq-gradient px-3 py-1.5 text-xs font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-50" data-testid={`connect-btn-${u.username}`}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><UserPlus className="h-3.5 w-3.5" /> Connect</>}
          </button>
        )}
      </div>
    </div>
  );
}

function NewChatDialog({ open, onOpenChange, onNewCircle }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["user-search", q],
    queryFn: () => api.searchUsers(q),
    enabled: open && q.trim().length >= 1,
  });

  const openMut = useMutation({
    mutationFn: (id) => api.openChat(id),
    onSuccess: (chat) => {
      qc.invalidateQueries({ queryKey: ["chats"] });
      onOpenChange(false);
      setQ("");
      navigate(`/app/chats/${chat.id || chat.conversation_id}`);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      if (d?.error === "dm_gate_unmet") {
        toast.error(d.message || (d.gate?.display ? `Hold ${d.gate.display} to start this chat` : "This person only accepts DMs from token holders."));
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Couldn't open the chat.");
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New conversation</DialogTitle>
        </DialogHeader>
        <button
          onClick={() => { onOpenChange(false); setQ(""); onNewCircle?.(); }}
          className="flex w-full items-center gap-3 rounded-xl border border-bloq-mint/25 bg-bloq-mint/[0.06] p-3 text-left transition-colors hover:bg-bloq-mint/10"
          data-testid="new-circle-entry"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bloq-mint/15 text-bloq-mint">
            <MessagesSquare className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white">New Circle</div>
            <div className="truncate text-xs text-bloq-muted">Start a small group (up to 20 people)</div>
          </div>
          <ChevronRight className="h-4 w-4 text-bloq-mint" />
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-bloq-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people by name or @username"
            className="h-11 w-full rounded-xl border border-white/10 bg-bloq-bg pl-9 pr-3 text-sm outline-none focus:border-bloq-mint/40"
            data-testid="user-search-input"
          />
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {isFetching && (
            <div className="flex justify-center py-6 text-bloq-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
          {!isFetching && q.trim().length >= 1 && results.length === 0 && (
            <p className="py-6 text-center text-sm text-bloq-muted">No people found.</p>
          )}
          {results.map((u) => (
            <SearchResultRow key={u.id} u={u} onOpen={(id) => openMut.mutate(id)} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChatList({ activeId }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [circleOpen, setCircleOpen] = useState(false);

  const { data: chats = [], isLoading } = useQuery({
    queryKey: ["chats"],
    queryFn: api.listChats,
  });
  // Chats tab is DMs only (v1.2.2) — group Circles live on their own tab.
  // Order: official @bloqtalk first, then user-pinned, then the rest (stable).
  const rank = (c) => (isOfficialUser(c.other) ? 4 : 0) + (c.pinned ? 2 : 0);
  const sortedChats = [...chats]
    .filter((c) => c.type !== "group")
    .sort((a, b) => rank(b) - rank(a));
  const { data: requests = [] } = useQuery({
    queryKey: ["requests"],
    queryFn: api.listRequests,
  });

  return (
    <div className="flex h-full min-h-0 w-full flex-col border-r border-white/8 bg-bloq-s1 md:w-[360px] md:shrink-0">
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <div>
          <h1 className="text-2xl font-bold">Chats</h1>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-mint">
            <Lock className="h-3 w-3" /> End-to-end encrypted
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton />
          <ActivityBell />
          <NotifBell />
          <button
            onClick={() => setDialogOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bloq-gradient text-bloq-bg transition-opacity hover:opacity-90"
            data-testid="new-chat-btn"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>

      <GettingStartedNudge />

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <StoriesBar />
        <WhatsNewCard />
        <ActivationChecklist />
        <InstallPrompt />
        {requests.length > 0 && (
          <button
            onClick={() => navigate("/app/requests")}
            className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-bloq-mint/25 bg-bloq-mint/10 p-3 text-left transition-colors hover:bg-bloq-mint/15"
            data-testid="connection-requests-banner"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bloq-mint/20 text-bloq-mint">
              <UserPlus className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-bloq-mint">
                {requests.length} connection request{requests.length > 1 ? "s" : ""}
              </div>
              <div className="truncate text-xs text-bloq-muted">
                Strangers can't DM you — Shield screens every request
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-bloq-mint" />
          </button>
        )}

        {isLoading ? (
          <div className="flex justify-center py-10 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : sortedChats.length === 0 ? (
          <ColdStartDiscover onStartChat={() => setDialogOpen(true)} />
        ) : (
          sortedChats.map((c) => {
            const isActive = c.id === activeId;
            const unread = c.unread > 0;
            const isGroup = c.type === "group";
            const official = !isGroup && isOfficialUser(c.other);
            const name = isGroup ? (c.name || "Circle") : c.other?.display_name;
            const rowKey = isGroup ? c.id : c.other?.username;
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/chats/${c.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/app/chats/${c.id}`); }}
                className={cn(
                  "group flex w-full cursor-pointer items-center gap-3 rounded-2xl p-3 text-left transition-colors",
                  isActive ? "bg-white/8" : unread ? "bg-bloq-mint/[0.04] hover:bg-white/5" : "hover:bg-white/5",
                )}
                data-testid={`chat-row-${rowKey}`}
              >
                <div className="relative shrink-0">
                  {isGroup ? (
                    c.icon_b64 ? (
                      <img src={c.icon_b64} alt="" className="h-12 w-12 rounded-2xl object-cover" />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bloq-mint/15 text-bloq-mint"><MessagesSquare className="h-5 w-5" /></span>
                    )
                  ) : (
                    <Avatar user={c.other} size="md" />
                  )}
                  {!isGroup && c.online && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-bloq-bg bg-bloq-mint"
                      title="Online now"
                      data-testid={`online-dot-${rowKey}`}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={cn("truncate font-semibold", unread ? "text-white" : "text-white/90")}>{name}</span>
                    {isGroup ? (
                      <span className="shrink-0 rounded-full bg-white/8 px-1.5 py-0.5 text-[10px] font-semibold text-bloq-muted">{c.member_count || (c.member_preview?.length ?? 0)}</span>
                    ) : (
                      <>
                        {c.other?.verified && <VerifiedCheck size={13} />}
                        {!official && <TopConnectorCrown match={c.other} iconOnly />}
                        {official && <OfficialBadge label={false} size={12} />}
                        {!official && c.other?.score_band && <TrustBand band={c.other.score_band} />}
                      </>
                    )}
                  </div>
                  <div className={cn("mt-0.5 truncate text-sm", unread ? "font-medium text-white/85" : "text-bloq-muted")} data-testid={`chat-preview-${rowKey}`}>
                    <LastPreview chat={c} />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {c.pinned && <Pin className="h-3 w-3 shrink-0 text-bloq-mint" data-testid={`chat-pinned-${rowKey}`} />}
                  <ChatRowActions chat={c} />
                  <div className="flex flex-col items-end gap-1">
                    <span className={cn("text-[11px]", unread ? "font-semibold text-bloq-mint" : "text-bloq-muted")}>{timeAgo(c.last_activity)}</span>
                    {unread && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-bloq-mint px-1.5 text-[11px] font-bold text-bloq-bg" data-testid={`unread-badge-${rowKey}`}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <NewChatDialog open={dialogOpen} onOpenChange={setDialogOpen} onNewCircle={() => setCircleOpen(true)} />
      <NewCircleDialog open={circleOpen} onOpenChange={setCircleOpen} />
    </div>
  );
}

export default function ChatsPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const storyReplyDraft = location.state?.storyReply || null;

  return (
    <div className="flex h-full min-h-0 w-full">
      {/* List: hidden on mobile when a chat is open; fixed width on desktop */}
      <div className={cn("h-full min-h-0", chatId ? "hidden md:flex" : "flex w-full md:w-[360px] md:shrink-0")}>
        <ChatList activeId={chatId} />
      </div>

      {/* Thread */}
      <div className={cn("h-full min-h-0 min-w-0 flex-1", chatId ? "flex" : "hidden md:flex")}>
        {chatId ? (
          <ChatThread conversationId={chatId} onBack={() => navigate("/app/chats")} initialDraft={storyReplyDraft} />
        ) : (
          <EmptyConversation />
        )}
      </div>
    </div>
  );
}

function EmptyConversation() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const go = (e) => {
    e.preventDefault();
    const v = q.trim();
    navigate(v ? `/app/search?q=${encodeURIComponent(v)}` : "/app/search");
  };
  return (
    <div className="hidden min-w-0 flex-1 flex-col bg-bloq-bg md:flex" data-testid="chats-empty-pane">
      {/* Global search bar */}
      <div className="border-b border-white/8 px-6 py-4">
        <form onSubmit={go} className="mx-auto flex max-w-xl items-center gap-2" data-testid="global-search-bar">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-bloq-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people, bloqs, channels, messages…"
              className="h-12 w-full rounded-2xl border border-white/12 bg-bloq-s1 pl-12 pr-4 text-sm text-white outline-none transition-colors focus:border-bloq-mint/40"
              data-testid="global-search-bar-input"
            />
          </div>
          <button
            type="submit"
            className="flex h-12 shrink-0 items-center gap-2 rounded-2xl bloq-gradient px-5 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
            data-testid="global-search-bar-submit"
          >
            <Search className="h-4 w-4" /> Search
          </button>
        </form>
      </div>
      {/* Centered empty message */}
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="text-center text-bloq-muted">
          <MessageCircle className="mx-auto mb-3 h-12 w-12 opacity-40" />
          <p>Select a conversation to start chatting</p>
        </div>
      </div>
    </div>
  );
}
